import React, { useState, useRef, useEffect, useContext } from 'react';
import Tagify from '@yaireo/tagify';
import { TypeFilter } from './TypeFilter';
import { List } from './List';
import { FilterType } from '../../model/FilterType';
import { FilterItem } from '../../model/FilterItem';
import { getGates, getLocations, getZones, getCrossingLines } from '../../model/Collapsed';
import { AccountContext } from '../../contexts/AccountContext';
import { Filter } from '../../model/Filter';
import { t } from '../../i18n';

// Al quitar un elemento se quitan también los que dependen de él en la
// jerarquía; la cascada sigue sola porque cada quitado dispara este mismo
// callback.
const childType = new Map<FilterType, FilterType>([
  [ FilterType.location, FilterType.installation ],
  [ FilterType.installation, FilterType.campaign ],
  [ FilterType.campaign, FilterType.playlist ]
]);

const Filter = ({ locationId, installationId, campaignId, playlistId, zone, gate, crossingLine, from, to, onChange }: Props) => {
  const tagifyElementRef = useRef<HTMLInputElement | null>(null);
  const tagifyRef = useRef<Tagify | null>(null);
  const selectedItemsRef = useRef<FilterItem[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const zonesOrGatesRequestRef = useRef(0);
  const zonesOrGatesLocationKeyRef = useRef('');
  const initialZoneAppliedRef = useRef(false);
  const initialGateAppliedRef = useRef(false);
  const initialCrossingLineAppliedRef = useRef(false);
  const [textFilter, setTextFilter] = useState('');
  const [display, setDisplay] = useState(false);
  const [selectedItems, setSelectedItems] = useState<FilterItem[]>([]);
  const [type, setType] = useState(FilterType.all);
  const [changed, setChanged] = useState(false);
  const [locations, setLocations] = useState<any[]>([]);
  const [installations, setInstallations] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [gates, setGates] = useState<any[]>([]);
  const [crossingLines, setCrossingLines] = useState<any[]>([]);
  const params = useContext(AccountContext);

  useEffect(() => {
    const tagTemplate = (tagData: any) => {
      return `<tag title="${tagData.text}" contenteditable="false" spellcheck="false" tabIndex="-1" class="tagify__tag" ${tagData.value === 'more' ? 'readonly="true' : ''}">
          <x title="" class="tagify__tag__removeBtn" role="button" aria-label="${t('filter.removeTag')}"></x>
          <div style="height: 24px; overflow: hidden;">
            <span class="tagify__tag-text">${tagData.text}</span>
          </div>
        </tag>`;
    }
    if(tagifyElementRef.current) {
      tagifyRef.current = new Tagify(tagifyElementRef.current, { 
        addTagOn: [],
        templates: {
          tag: tagTemplate
        },
        callbacks: {
          input: e => {
            const detail: any = e.detail;
            setTextFilter(detail.value);
            if(detail.value) {
              setDisplay(true);
            }      
          },
          remove: e => {
            if(e.detail.data.value !== 'more') {
              setSelectedItems(selectedItemsRef.current.filter(i => i.value !== e.detail.data.value));
              setChanged(true);
              const child = childType.get(e.detail.data.type);
              child && removeSelectedItems(child);
            }
          },
          focus: () => {
            setDisplay(true);
          }
        } 
      });
    }
    document.addEventListener('click', e => {
      const target: any = e.target;
      if(containerRef.current && !target.classList.contains('view-more') && !containerRef.current.contains(target)) {
        setDisplay(false);
      }
    });
  }, []);

  useEffect(() => {
    const load = async () => {
      const installations = new Map<string, any>();
      const campaigns = new Map<string, any>();
      const playlists = new Map<string, any>();
      const locations = await getLocations({ from, to }, params);
      for(let location of locations) {
        if(locationId?.includes(location.id) && !selectedItemsRef.current.some(item => item.value === location.id)) {
          onAdd({ value: location.id, type: FilterType.location, text: location.name });
        }
        for(let installation of location.installations) {
          if(!installations.has(installation.id)) {
            installations.set(installation.id, installation);
          }
          if(installationId && installationId === installation.id && !selectedItemsRef.current.some(item => item.value === installation.id)) {
            onAdd({ value: installation.id, type: FilterType.installation, text: installation.name });
          }
          for(let campaign of installation.campaigns) {
            if(!campaigns.has(campaign.id)) {
              campaigns.set(campaign.id, campaign);
            }
            if(campaignId && campaignId === campaign.id && !selectedItemsRef.current.some(item => item.value === campaign.id)) {
              onAdd({ value: campaign.id, type: FilterType.campaign, text: campaign.name });
            }
            for(let playlist of campaign.playlists || []) {
              if(!playlists.has(playlist.id)) {
                playlists.set(playlist.id, playlist);
              }
              if(playlistId && playlistId === playlist.id && !selectedItemsRef.current.some(item => item.value === playlist.id)) {
                onAdd({ value: playlist.id, type: FilterType.playlist, text: playlist.name });
              }
            }
          }
        }
      }
      setChanged(false);
      setLocations(locations);
      setInstallations(Array.from(installations.values()));
      setCampaigns(Array.from(campaigns.values()));
      setPlaylists(Array.from(playlists.values()));
    };
    load();
  }, [ from, to ]);

  useEffect(() => {
    selectedItemsRef.current = selectedItems;
    const locationIds = selectedItems
      .filter(item => item.type === FilterType.location)
      .map(item => item.value);
    if(!locationIds.length && (type === FilterType.zone || type === FilterType.gate || type === FilterType.crossingLine)) {
      setType(FilterType.all);
    }
    if(!locationIds.length) {
      zonesOrGatesLocationKeyRef.current = '';
      zonesOrGatesRequestRef.current++;
      setZones([]);
      removeSelectedItems(FilterType.zone);
      setGates([]);
      removeSelectedItems(FilterType.gate);
      setCrossingLines([]);
      removeSelectedItems(FilterType.crossingLine);
      return;
    }
    const locationKey = locationIds.join('|');
    if(zonesOrGatesLocationKeyRef.current === locationKey) {
      return;
    }
    zonesOrGatesLocationKeyRef.current = locationKey;
    setZones([]);
    setGates([]);
    setCrossingLines([]);
    const requestId = ++zonesOrGatesRequestRef.current;
    const loadZones = async () => {
      try {
        const zones = await getZones({ locationId: locationIds, from: from, to: to }, params);
        if(requestId !== zonesOrGatesRequestRef.current) {
          return;
        }
        console.log('zones', zones);
        setZones(zones);
        const availableZones = zones.map(zone => zone.id);
        for(let selectedZone of selectedItemsRef.current.filter(item => item.type === FilterType.zone)) {
          if(!availableZones.includes(selectedZone.value)) {
            onRemove(selectedZone);
          }
        }
        if(zone && !initialZoneAppliedRef.current) {
          initialZoneAppliedRef.current = true;
          const initialZone = zones.find(z => z.name === zone);
          if(initialZone && !selectedItemsRef.current.some(item => item.type === FilterType.zone && item.value === zone)) {
            onAdd({ value: initialZone.id, type: FilterType.zone, text: initialZone.name });
          }
        }
      }
      catch(err) {
        console.error('Error loading zones', err);
        if(requestId === zonesOrGatesRequestRef.current) {
          setZones([]);
        }
      }
    };
    const loadGates = async () => {
      try {
        const gates = await getGates({ locationId: locationIds, from: from, to: to }, params);
        if(requestId !== zonesOrGatesRequestRef.current) {
          return;
        }
        console.log('gates', gates);
        setGates(gates);
        const availableGates = gates.map(gate => gate.id);
        for(let selectedGate of selectedItemsRef.current.filter(item => item.type === FilterType.gate)) {
          if(!availableGates.includes(selectedGate.value)) {
            onRemove(selectedGate);
          }
        }
        if(gate && !initialGateAppliedRef.current) {
          initialGateAppliedRef.current = true;
          const initialGate = gates.find(g => g.name === gate);
          if(initialGate && !selectedItemsRef.current.some(item => item.type === FilterType.gate && item.value === gate)) {
            onAdd({ value: initialGate.id, type: FilterType.gate, text: initialGate.name });
          }
        }
      }
      catch(err) {
        console.error('Error loading gates', err);
        if(requestId === zonesOrGatesRequestRef.current) {
          setGates([]);
        }
      }
    };
    const loadCrossingLines = async () => {
      try {
        const crossingLines = await getCrossingLines({ locationId: locationIds, from: from, to: to }, params);
        if(requestId !== zonesOrGatesRequestRef.current) {
          return;
        }
        console.log('crossing lines', crossingLines);
        setCrossingLines(crossingLines);
        const availableCrossingLines = crossingLines.map(crossingLine => crossingLine.id);
        for(let selectedCrossingLine of selectedItemsRef.current.filter(item => item.type === FilterType.crossingLine)) {
          if(!availableCrossingLines.includes(selectedCrossingLine.value)) {
            onRemove(selectedCrossingLine);
          }
        }
        if(crossingLine && !initialCrossingLineAppliedRef.current) {
          initialCrossingLineAppliedRef.current = true;
          const initialCrossingLine = crossingLines.find(l => l.name === crossingLine);
          if(initialCrossingLine && !selectedItemsRef.current.some(item => item.type === FilterType.crossingLine && item.value === crossingLine)) {
            onAdd({ value: initialCrossingLine.id, type: FilterType.crossingLine, text: initialCrossingLine.name });
          }
        }
      }
      catch(err) {
        console.error('Error loading crossing lines', err);
        if(requestId === zonesOrGatesRequestRef.current) {
          setCrossingLines([]);
        }
      }
    };
    loadZones();
    loadGates();
    loadCrossingLines();
  }, [ selectedItems ]);

  useEffect(() => {
    const tags = tagifyRef.current;
    if(tags) {
      const tagElements = tags.getTagElms();
      if(!display) {
        const inputWidth = 50;
        let widthAvailable = tags.DOM.scope.clientWidth - inputWidth;
        let showEllipsis = false;
        for(let tag of tagElements) {
          widthAvailable -= tag.clientWidth;
          if(widthAvailable <= 0) {
            tag.style.display = 'none';
            showEllipsis = true;
          }
        }
        if(showEllipsis) {
          tags.addTags([{ value: 'more', text: '...' }]);
        }
      }
      else {
        tags.removeTags('more');
        for(let tag of tagElements) {
          tag.style.display = 'flex';
        }
      }
    }
  }, [ display ]);

  const onToggle = () => {
    setDisplay(!display);
  }

  const onChangeType = (type: FilterType) => {
    setType(type);
  }

  const onAdd = (item: FilterItem) => {
    tagifyRef.current && tagifyRef.current.addTags([ { ...item, editable: false } ]);
    selectedItems.push(item);
    setSelectedItems([ ...selectedItems ]);
    setChanged(true);
  }

  const onRemove = (item: FilterItem) => {
    tagifyRef.current && tagifyRef.current.removeTags(item.value);
  }

  const showZonesAndGates = selectedItems.some(item => item.type === FilterType.location);

  const removeSelectedItems = (type: FilterType) => {
    const itemsToRemove = selectedItemsRef.current.filter(i => i.type === type);
    for(let item of itemsToRemove) {
      onRemove(item);
    }
  }

  const onApply = () => {
    const locations = getSelectedMany(FilterType.location);
    const installation = getSelected(FilterType.installation);
    const campaign = getSelected(FilterType.campaign);
    const playlist = getSelected(FilterType.playlist);
    const zone = getSelected(FilterType.zone);
    const gate = getSelected(FilterType.gate);
    const crossingLine = getSelected(FilterType.crossingLine);
    const locationIds = locations.map(l => l.id);
    const locationNames = locations.map(l => l.name);
    onChange(locationIds.length ? locationIds : undefined, locationNames.length ? locationNames : undefined, installation.id, installation.name, campaign.id, campaign.name, playlist.id, playlist.name, zone.id, gate.id, crossingLine.id);
    setDisplay(false);
    setChanged(false);
  }

  const getSelected = (type: FilterType): { id: string, name: string } => {
    for(let s of selectedItemsRef.current) {
      if(s.type === type) {
        return { id: s.value, name: s.text };
      }
    }
    return { id: '', name: '' };
  }

  const getSelectedMany = (type: FilterType): { id: string, name: string }[] => {
    return selectedItemsRef.current
      .filter(s => s.type === type)
      .map(s => ({ id: s.value, name: s.text }));
  }

  return (
    <div ref={containerRef} className="d-flex align-items-center ps-2 pe-2">
      <div className="flex-grow-1">
        <div className="position-relative z-2">
          <div className="position-absolute w-100" style={{ marginTop: -18.85, height: 37.6 }}>
            <div className="d-flex w-100">
              <div className={`input-group input-group-merge ${display ? 'tags-expanded' : 'tags-collapsed'}`}>
                <span className="input-group-text">
                  <i className="ti ti-search" />
                </span>
                <input ref={tagifyElementRef} className="form-control flex-grow-1 d-flex" />
                <span className="input-group-text" onClick={onToggle}>
                  <i className="ti ti-chevron-down" />
                </span>
              </div>
              <button type="button" className={`btn btn-primary waves-effect ms-1 ${!changed ? 'd-none' : '' }`} style={{ height: 37.6 }} onClick={onApply}>{t('common.apply')}</button>
              <button type="button" className={`btn btn-label-secondary ms-1 ps-2 pe-2 ${changed || !selectedItemsRef.current.length ? 'd-none' : '' }`} style={{ height: 37.6 }} onClick={onToggle}>
                <i className="ti ti-circle-plus"></i>
              </button>
            </div>
            <div className={`card card-body w-100 ${!display ? 'd-none' : ''}`}>
              <div className="d-flex">
                <TypeFilter type={type} showZonesAndGates={showZonesAndGates} onChange={onChangeType} />
              </div>
              <List textFilter={textFilter} type={type} locations={locations} installations={installations} campaigns={campaigns} playlists={playlists} zones={zones} gates={gates} crossingLines={crossingLines} selected={selectedItems} showZonesAndGates={showZonesAndGates} onAdd={onAdd} onRemove={onRemove} onChangeType={onChangeType} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  locationId?: string[],
  installationId?: string,
  campaignId?: string,
  playlistId?: string,
  zone?: string,
  gate?: string,
  crossingLine?: string,
  from?: Date,
  to?: Date,
  onChange: (locationId?: string[], locationName?: string[], installationId?: string, installationName?: string, campaignId?: string, campaignName?: string, playlistId?: string, playlistName?: string, zone?: string, gate?: string, crossingLine?: string) => void
}

export { Filter };
